/**
 * FloatingPreviewWindow — the draggable/resizable multitask window chrome
 * (2026-08-02 directive; same-day amendment: sizing is ENTIRELY customizable —
 * every edge and corner resizes, plus maximize/restore and double-click-header
 * maximize). Content is the shared PreviewBody engine (attachments) or the
 * sandboxed 3D scene (resolver-backed).
 *
 * It only draws: the drag, the resize, maximize and restore, the step aside for a docked chat and the
 * recovery on a resize of the screen are src/hooks/filePreview/useFloatingPreviewWindow.ts, the content
 * usePreview, the scene useThreeDScene (PV5-8:C part 1, 2026-10-13).
 */
import React from 'react';
import styled from 'styled-components';
import { PreviewBody, ToolButton, FullFrame } from './PreviewContent';
import { injectRuntimeGuard } from './sceneRuntimeGuard';
import { OpenChatBesideButton } from './previewOrigin';
import { usePreview } from '../../hooks/filePreview/usePreview';
import type { PreviewOriginContext } from '../../hooks/filePreview/previewOrigin';
import type { FloatingWindowState } from '../../hooks/filePreview/floatingPreviews';
import {
  RESIZE_HANDLES,
  useFloatingPreviewWindow,
  useThreeDScene,
} from '../../hooks/filePreview/useFloatingPreviewWindow';
import type { WindowRect } from './windowGeometry';

const Shell = styled.div`
  position: fixed;
  background: #101623; color: #e8edf6;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  cursor: move; user-select: none; flex: 0 0 auto;
  touch-action: none;
`;

const Title = styled.div`
  flex: 1; min-width: 0; font-size: 13px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const Body = styled.div`
  flex: 1; min-height: 0; display: flex; position: relative; background: #0a0f1a;
`;

/** Grab strips on every edge + corner (entirely-customizable sizing). */
const EdgeHandle = styled.div<{ $cursor: string }>`
  position: absolute; z-index: 3; touch-action: none;
  cursor: ${(p) => p.$cursor};
`;

const CornerGlyph = styled.div`
  position: absolute; right: 4px; bottom: 4px; width: 8px; height: 8px;
  pointer-events: none;
  border-right: 2px solid rgba(255, 255, 255, 0.35);
  border-bottom: 2px solid rgba(255, 255, 255, 0.35);
`;

const Center = styled.div`
  margin: auto; color: #93a1b8; font-size: 13px; text-align: center; padding: 18px;
`;

const AttachmentContent: React.FC<{
  file?: File | null;
  serialized?: any;
  context?: PreviewOriginContext | null;
}> = ({ file, serialized, context }) => {
  // context restored 2026-08-24: without it a floated preview could not use
  // the server's locator-free resolve (name + messageId), so a metadata-only
  // attachment degraded to the download card the moment it was popped out.
  const p = usePreview(file, serialized, context);
  return <PreviewBody p={p} wrap />;
};

const ThreeDContent: React.FC<{ artifactId: string }> = ({ artifactId }) => {
  const { scene, failure } = useThreeDScene(artifactId);
  if (failure) return <Center>This 3D model could not be loaded.</Center>;
  if (!scene) return <Center>Loading 3D model…</Center>;
  return (
    <FullFrame
      title={scene.title || 'Interactive 3D model'}
      sandbox="allow-scripts allow-pointer-lock"
      // Sandboxed frames have an opaque origin, so the pointer-lock
      // permissions-policy must be granted with * — without it Chrome
      // (incognito especially) rejects requestPointerLock silently and
      // "click to walk through" does nothing.
      allow="pointer-lock *; fullscreen *"
      // R13: guard injected like Preview3DPage and PreviewContent's scene3d
      // lane — a scene that dies at boot must explain itself in every shell
      // (2026-08-09 "failure has no face" contract).
      srcDoc={injectRuntimeGuard(scene.html)}
    />
  );
};

export interface FloatingPreviewWindowProps {
  state: FloatingWindowState;
  onClose: () => void;
  onFocus: () => void;
  onRect: (rect: WindowRect) => void;
}

export const FloatingPreviewWindow: React.FC<FloatingPreviewWindowProps> = ({ state, onClose, onFocus, onRect }) => {
  const { rect, z, payload } = state;
  const w = useFloatingPreviewWindow({ rect, onFocus, onRect });

  const subtitle = payload.kind === 'attachment'
    ? undefined
    : 'Interactive 3D model';

  return (
    <Shell
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, zIndex: z }}
      onPointerDown={onFocus}
      role="dialog"
      aria-label={`Floating preview: ${payload.title}`}
      // portalClickGuard marker: interacting with a floating preview must never
      // read as an outside click that closes/collapses the chat (2026-08-02).
      data-prest0n-portal="true"
    >
      <Header
        onPointerDown={w.beginGesture('drag')}
        onPointerMove={w.onPointerMove}
        onPointerUp={w.endGesture}
        onPointerCancel={w.endGesture}
        onDoubleClick={w.toggleMaximize}
      >
        <Title title={payload.title}>{payload.title}{subtitle ? ` — ${subtitle}` : ''}</Title>
        {/* The file's own conversation, beside it — same control, same copy
            as the modal shell (previewOrigin.tsx). Only attachments carry an
            origin; a 3D artifact window has no conversation behind it. */}
        {payload.kind === 'attachment' && (
          <OpenChatBesideButton origin={payload.context} onOpened={w.stepAsideForChat} />
        )}
        <ToolButton
          onPointerDown={w.stopDrag}
          onClick={w.toggleMaximize}
          aria-label={w.isMaximized ? 'restore window size' : 'maximize window'}
        >
          {w.isMaximized ? 'Restore' : 'Maximize'}
        </ToolButton>
        <ToolButton
          onPointerDown={w.stopDrag}
          onClick={w.fullscreen}
        >
          Fullscreen
        </ToolButton>
        <ToolButton
          onPointerDown={w.stopDrag}
          onClick={onClose}
          aria-label="close floating preview"
        >
          ✕
        </ToolButton>
      </Header>
      <Body ref={w.bodyRef}>
        {payload.kind === 'attachment'
          ? <AttachmentContent file={payload.file} serialized={payload.serialized} context={payload.context} />
          : <ThreeDContent artifactId={payload.artifactId} />}
      </Body>
      {RESIZE_HANDLES.map(({ edge, cursor, style }) => (
        <EdgeHandle
          key={edge}
          $cursor={cursor}
          style={style}
          data-resize-edge={edge}
          onPointerDown={w.beginGesture('resize', edge)}
          onPointerMove={w.onPointerMove}
          onPointerUp={w.endGesture}
          onPointerCancel={w.endGesture}
          aria-label={`resize ${edge}`}
        />
      ))}
      <CornerGlyph />
    </Shell>
  );
};

export default FloatingPreviewWindow;
